// apiService.js

import { Issuer } from '../api/center';
import {
    getAllMembershipForMember,
    getIsNewSales,
    getMembershipForMember,
    getUnpaidPayments,
    GetUnpaidPaymentsApiType,
} from '../api/memberShip';
import { SalesManager, SalesStatus } from './useMembershipPaymentsQueryService';

export const fetchAllMembershipForMember = (
    params: {
        coachId: string;
        memberId: string;
        accessToken: string;
    },
    authorization: string,
) => ({
    queryKey: ['getAllMembershipForMemberQuery', params.coachId, params.memberId],
    queryFn: () => getAllMembershipForMember(params.coachId, params.memberId, authorization),
    onSuccess: (data: any) => {
        // 데이터 정제 로직
        return data?.data?.membersMemberships ?? [];
    },
});
export const fetchMembershipForMember = (
    params: {
        membershipId: string;
        /** 'group'이면 MemberTicket detail 엔드포인트로 분기 (커밋 6bf11df5) */
        source?: 'pt' | 'group';
        /** source='group'일 때 필요 */
        centerId?: string;
    },
    authorization: string,
) => ({
    queryKey: ['getMembershipForMemberQuery', params.membershipId, params.source ?? 'pt'],
    queryFn: () =>
        getMembershipForMember(params.membershipId, authorization, params.source, params.centerId),
    onSuccess: (data: any) => {
        // 데이터 정제 로직
        return data?.data?.membership ?? [];
    },
});
//getIsNewSales
export const fetchGetIsNewSales = (
    params: {
        memberId: string;
        coachId: string;
    },
    authorization: string,
) => ({
    queryKey: ['getIsNewSalesQuery', params.memberId, params.coachId],
    queryFn: async () => await getIsNewSales(params.coachId, params.memberId, authorization),
    onSuccess: (data: any) => {
        // 데이터 정제 로직
        return data?.data ?? undefined;
    },
});
export const fetchGetUnpaidPayments = (params: GetUnpaidPaymentsApiType, authorization: string) => ({
    queryKey: ['getUnpaidPaymentsQuery', ...Object.values(params)],
    queryFn: async () => {
        const response = await getUnpaidPayments({ ...params, accessToken: authorization });
        if (response?.status === 503 && response.data?.errorCode === 'UNPAID_LIST_TEMPORARILY_UNAVAILABLE') {
            throw Object.assign(new Error('잠시 후 다시 시도해 주세요'), { status: 503, data: response.data });
        }
        return response;
    },
    onSuccess: (data: any) => {
        // 데이터 정제 로직
        return data?.data ?? undefined;
    },
});

/**
 * 회원권
 * */
/**
 * 수업별 뷰(tableDataBySchedule) row의 그룹 session 확장용 member 상세.
 * 서버 getCenterMembersByMembershipForCenter → 그룹 row의 members[] 배열로 내려온다.
 * (커밋 e06ffdc6)
 */
export type GroupSessionMemberDetail = {
    memberId: string;
    memberName: string;
    attendanceStatus: 'ATTENDANCE' | 'NOSHOW' | 'PENALTY_CANCELLED';
    membershipName: string;
    currentSession: number;
    totalSession: number;
    pricePerSchedule: number;
    memberScheduleFee: number;
    commission: number;
    commissionType: 'percentage' | 'fixed';
    cumulativeSessionCount?: number | null;
    isNewSales: boolean;
    /** 상세 정산 — 노쇼라서 근무 시간 밖인데 IN 으로 계산한 예약에만 'noshow' */
    inOutForcedBy?: 'noshow';
    /** 위 예약 중 IN 요율이 0이라 0원이 된 예약에만 true */
    forcedInZeroRate?: boolean;
};

export type MemberMembershipDataType = {
    _id: string;
    /** 인계로 생성된 사본이면 원래 수강권 ID가 내려온다. */
    transferredFromMembershipId?: string;
    period: number;
    isDeleted: boolean;
    isCanceled: boolean;
    coachId: string;
    memberId: string;
    name: string;
    activationDate: string;
    expirationDate: string;
    startSessionValue: number;
    totalSession: number;
    createdAt: string;
    updatedAt: string;
    __v: number;
    BGColor: string[];
    issuer: Issuer;
    membershipTemplateId: string;
    /** 공유 상품 발급 시점의 불변 정책 스냅샷. 참여 상태는 share pool에서 조회한다. */
    sharePolicySnapshot?: {
        enabled: true;
        membershipTemplateId: string;
        maxParticipants: number;
        maxLifetimeParticipants: number;
        capturedAt: string;
    };
    memo: string;
    pricePerSchedule: number;
    status: 'expired' | 'active';
    totalPrice: number;
    currentSession: number;
    /** 종료된 수강권 상세에는 이 키가 없다. `archivedScheduleHistory` 를 대신 읽는다. */
    schedules?: MemberScheduleDataType[];
    remainSession: number;
    commissionType: string;
    commission: number | null;
    coachName?: string;
    memberName?: string;
    attendanceStatus?: 'ATTENDANCE' | 'NOSHOW';
    startTime?: string;
    membershipName?: string;
    memberScheduleFee?: number;
    isNewSales?: boolean; // 신규 수강권이면 True 재등록이면 false
    initialPaymentDate?: string; // 결제 일자
    salesManager?: SalesManager;
    paymentHistory?: PaymentHistoryType[];
    totalUnpaidAmount?: number;
    totalPaymentAmount?: number;
    isSplitPayment?: boolean;
    inOutType?: 'IN' | 'OUT';
    /**
     * 상세 정산 「시간 상관없이 IN」 — 근무 시간으로는 OUT 인데 IN 으로 계산했을 때만 온다.
     * 'noshow': 노쇼 IN 설정 / 'exceptionRule': 「항상 IN 요율의 배수」 예외. 그때 timeInOutType 은 근무 시간 판정(OUT).
     */
    inOutForcedBy?: 'noshow' | 'exceptionRule';
    timeInOutType?: 'IN' | 'OUT';
    /** 위처럼 IN 으로 계산했는데 IN 요율이 0이라 0원이 된 수업에만 true */
    forcedInZeroRate?: boolean;
    /** 「항상 IN 요율의 배수」 예외가 적용된 행에만 'inMultiplier'(배수는 exceptionMultiplier) */
    exceptionMode?: 'inMultiplier';
    isCompositeRate?: boolean;
    effectiveCommissionRate?: number | null;
    cumulativeSessionCount?: number | null;
    /**
     * 이 수업에 수강권별 커스텀 요율이 적용됐는지. tier/sessionTier bySchedule row에 additive로 추가 예정.
     * (PLAN_TIER_CUSTOM_COMMISSION §12.4-1) 미전달 시 undefined → 수업별 탭 아이콘 미표시.
     */
    isCustomedCommission?: boolean;
    /**
     * 그룹수업 통합(ReservationRules.includeGroupClassInSettlement) ON 시 세션 단위 row 구분자.
     * 서버 커밋 e06ffdc6에서 추가. 'group'일 때 members[]가 채워진다.
     */
    source?: 'pt' | 'group';
    /**
     * 만료 정책 — 2026-04 도메인 통일 작업에서 추가.
     *  - 'COUNT'  : 회차 소진 시 만료 (PT default — 기존 동작 유지)
     *  - 'PERIOD' : 기간 만료 시 만료
     *  - 'FIRST'  : 둘 중 먼저 도래 시 만료
     * 백엔드 `BACKEND_RESPONSE_MEMBERSHIP_UNIFIED_V1.md` §1 REQ-1/REQ-2 참조.
     */
    expiryPolicy?: 'COUNT' | 'PERIOD' | 'FIRST';
    /** 세션 내 참여 회원 수 (그룹 row 전용) */
    memberCount?: number;
    /** 세션 내 출석 회원 수 (그룹 row 전용) */
    attendanceCount?: number;
    /** 세션 내 노쇼 회원 수, 또는 그룹 수강권 상세의 노쇼 건수 요약 */
    noshowCount?: number;
    /** 세션 내 패널티 취소 회원 수 (그룹 row 전용) */
    penaltyCount?: number;
    /** 그룹 세션에 참여한 회원별 상세. 펼침 UI 용도. */
    members?: GroupSessionMemberDetail[];
    /** 그룹 수강권 상세 요약 */
    activeReservationCount?: number;
    waitlistedCount?: number;
    completedCount?: number;
    restoredCancelCount?: number;
    lateCancelledCount?: number;
    noshowPenaltyCount?: number;
    /** 탈퇴 회원의 수강권 상세는 기록 보존을 위해 조회 전용이다. */
    appAccountStatus?: 'linked' | 'withdrawn' | 'unlinked';
    appWithdrawnAt?: string | null;
    appWithdrawalAuthStatus?: 'pending' | 'revoked' | null;
    isReadOnly?: boolean;
    /**
     * 종료(소프트 삭제)된 수강권 표식. **종료일 때만** 서버가 채운다.
     * 구 백엔드는 상세 자체를 404 로 막으므로 이 화면에서 undefined 는 "정상 수강권"이다.
     * 읽는 쪽은 `lib/membershipLifecycle.getMembershipLifecycle` 을 쓴다.
     */
    deletedContext?: 'HANDOFF' | 'ARCHIVED';
    /** 인계로 종료된 경우 이어지는 현재 수강권. 계보를 못 풀면 없다. */
    currentMembershipId?: string;
    /** 화면이 렌더하면 안 되는 액션. 프론트가 자체 규칙을 만들지 않는다. */
    lifecycleBlockedActions?: string[];
    /**
     * 종료된 수강권의 수업 이력(읽기 전용).
     *
     * 종료된 수강권 상세에는 `schedules` 가 **오지 않는다** — 구 화면이 그 배열을 클릭 가능한
     * 행으로 그리고 일정 수정 모달까지 열기 때문이다. 같은 내용이 이 키로 온다.
     */
    archivedScheduleHistory?: MemberScheduleDataType[];
};

/** 회원권의 스케줄 */
export type MemberScheduleDataType = {
    __v: number;
    _id: string;
    scheduleId?: string;
    alias: string;
    attendanceStatus: 'ATTENDANCE' | 'NOSHOW' | 'RESERVED' | 'WAITLISTED' | 'CANCELLED';
    coachId: string;
    conditionId: string;
    createdAt: string;
    currentSession: number;
    endTime: string;
    isCanceled: boolean;
    isDeleted: boolean;
    memberId: string;
    membershipId: string;
    membership: MemberMembershipDataType;
    memo: string;
    postId: string;
    requestDeadline: string;
    scheduleBundleId: string | null;
    startTime: string;
    totalSession: number;
    updatedAt: string;
    commission?: number;
    commissionType?: string;
    coachName?: string;
    memberName?: string;
    membershipName?: string;
    pricePerSchedule?: number;
    memberScheduleFee?: number;
    cumulativeSessionCount?: number | null;
    sessionTitle?: string;
    bookingStatus?: 'CONFIRMED' | 'WAITLISTED' | 'CANCELLED' | 'NOSHOW' | 'COMPLETED';
    bookedAt?: string;
    cancelledAt?: string;
    isLateCancel?: boolean;
    isPenaltyExempt?: boolean;
    countConsumedOnReserve?: boolean | null;
    countRestored?: boolean;
    penaltyCountApplied?: number;
    countImpactValue?: number | null;
    countImpactLabel?: string;
    countImpactType?: 'deducted' | 'restored' | 'none' | 'unknown';
};

export type PaymentHistoryType = {
    _id: string;
    paymentAmount: number;
    cancelAmount: number;
    saleStatus: SalesStatus;
    paymentDate: string;
    cancelDate: string;
    totalSession: number;
    totalSessionOffset: number;
    /**
     * 신규 원매출 환불 API 가 만든 환불행 표식.
     *
     * 이 행은 회차를 "현재 수강권"에서 깎고 원판매 totalPrice 는 보존한다. 구 결제행
     * 수정·삭제 API 로 다루면 회차가 두 번 복원되므로 서버가 409 REFUND_ROW_IMMUTABLE 로
     * 막는다. 화면은 진입 자체를 막는다(P14). 레거시 환불행에는 없다.
     */
    refundOperationId?: string;
    /**
     * 이 결제행이 기록된 수강권. 인계 계보 결제 목록(chainPaymentHistory)에만 온다.
     * 상세가 보고 있는 수강권과 다르면 회차 수정이 저쪽 문서에 적용된다(A6).
     */
    membershipId?: string;
};

export type UnpaidPaymentTableType = {
    carriedOutWithCoach?: { at: string };
    totalUnpaidAmount: number;
    totalPaymentAmount: number;
    recentPaymentDate: string;
    isNewSales: boolean;
    source?: 'pt' | 'group'; // 미수금 출처 — 그룹 row만 'group' 명시, PT row는 미설정(기본값)
    totalSession: number;
    name: string;
    status: string;
    membershipTemplateId: string;
    salesManager: SalesManager;
    membershipId: string;
    coachName: string;
    memberName: string;
    activationDate: string;
    expirationDate: string;
    totalPrice: number;
    BGColor: string[];
};

type SummaryData = {
    totalUnpaidPayment: number;
    totalUnpaidPaymentCount: number;
};

type FilterMember = {
    memberId: string;
    memberName: string;
};

type Filters = {
    salesManagers: SalesManager[];
    memberList: FilterMember[];
    membershiptemplateList: {
        membershipTemplateId: string;
        membershipTemplateName: string;
    }[];
};

export type UnpaidPaymentData = {
    tableDataByMembership: UnpaidPaymentTableType[];
    summaryData: SummaryData;
    filters: Filters;
};
